/**
 * API client for Creative Boards
 * Epic 007 - Story E007-S002
 */
import type {
  BoardListResponse,
  BoardResponse,
  BoardDetail,
  CreateBoardRequest,
  UpdateBoardRequest,
  CreateCardRequest,
  UpdateCardRequest,
  MoveCardRequest,
  CardWithMetrics,
  SuccessResponse,
} from '../types/boards';

const API_BASE = 'creative-boards';

/**
 * Handle API errors and throw with message
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

// ============================================================================
// BOARD OPERATIONS
// ============================================================================

/**
 * Get list of all boards for current user's organization
 */
export async function getBoards(): Promise<BoardListResponse> {
  const response = await fetch(API_BASE, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Send cookies for auth
  });
  return handleResponse<BoardListResponse>(response);
}

/**
 * Get single board with full details (columns + cards)
 */
export async function getBoard(boardId: string): Promise<BoardDetail> {
  const response = await fetch(`${API_BASE}/${boardId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return handleResponse<BoardDetail>(response);
}

/**
 * Create a new board
 * Automatically creates 4 default columns: Ideas, Testing, Winners, Archive
 */
export async function createBoard(data: CreateBoardRequest): Promise<BoardResponse> {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return handleResponse<BoardResponse>(response);
}

/**
 * Update board metadata (name, description, sharing)
 */
export async function updateBoard(
  boardId: string,
  data: UpdateBoardRequest
): Promise<BoardResponse> {
  const response = await fetch(`${API_BASE}/${boardId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return handleResponse<BoardResponse>(response);
}

/**
 * Delete board (soft delete)
 */
export async function deleteBoard(boardId: string): Promise<SuccessResponse> {
  const response = await fetch(`${API_BASE}/${boardId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return handleResponse<SuccessResponse>(response);
}

// ============================================================================
// CARD OPERATIONS
// ============================================================================

/**
 * Add a card to a column
 * @param columnId - Column to add card to
 * @param data - Card data
 */
export async function addCard(
  columnId: string,
  data: Omit<CreateCardRequest, 'column_id'>
): Promise<CardWithMetrics> {
  const response = await fetch(`${API_BASE}/columns/${columnId}/cards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ ...data, column_id: columnId }),
  });
  return handleResponse<CardWithMetrics>(response);
}

/**
 * Move a card to new column/position (drag and drop)
 * 
 * Handles:
 * - Same column reordering
 * - Cross-column moves
 * - Automatic position shifting
 * 
 * @param cardId - Card to move
 * @param data - Destination column + position
 */
export async function moveCard(
  cardId: string,
  data: MoveCardRequest
): Promise<CardWithMetrics> {
  const response = await fetch(`${API_BASE}/cards/${cardId}/move`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return handleResponse<CardWithMetrics>(response);
}

/**
 * Update card notes
 */
export async function updateCard(
  cardId: string,
  data: UpdateCardRequest
): Promise<CardWithMetrics> {
  const response = await fetch(`${API_BASE}/cards/${cardId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return handleResponse<CardWithMetrics>(response);
}

/**
 * Delete a card from board
 * Automatically reorders remaining cards in column
 */
export async function deleteCard(cardId: string): Promise<SuccessResponse> {
  const response = await fetch(`${API_BASE}/cards/${cardId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return handleResponse<SuccessResponse>(response);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get performance score color
 * Used for card badges
 */
export function getPerformanceColor(score: number | null): string {
  if (score === null) return 'gray';
  if (score >= 80) return 'green';
  if (score >= 60) return 'yellow';
  return 'red';
}

/**
 * Get platform icon name
 * Used for displaying platform logos
 */
export function getPlatformIcon(platform: string): string {
  const icons: Record<string, string> = {
    meta: '📘', // Facebook logo
    google: '🔍', // Google logo
    tiktok: '🎵', // TikTok logo
  };
  return icons[platform] || '📊';
}

/**
 * Format card metrics for display
 */
export function formatCardMetrics(card: CardWithMetrics): {
  roas: string;
  ctr: string;
  spend: string;
} {
  return {
    roas: card.roas ? `${card.roas.toFixed(2)}x` : 'N/A',
    ctr: card.ctr ? `${(card.ctr * 100).toFixed(2)}%` : 'N/A',
    spend: card.spend ? `$${card.spend.toLocaleString()}` : 'N/A',
  };
}
